/**
 * The PWA offline capture queue (§8.1, §13.6).
 *
 * §8.1 requires captures to survive whatever the network is doing — "holds captures in
 * IndexedDB when a request fails and flushes on reconnect" — under the §0 guarantee that
 * nothing captured is ever lost, not even underground or on a plane. A capture that only
 * lived in a React component's state would not survive a tab reload while offline, which is
 * exactly the case this exists for.
 *
 * This module was built before `/api/capture` existed (§11 item 7) and is deliberately
 * usable without it. A flush attempt against a missing endpoint fails
 * exactly like a flush attempt against no network at all, and `offlineOutbox.ts` already
 * treats any failure as "leave it queued, try again later." Nothing here assumes the
 * endpoint exists, and nothing changes the day it does. See DECISIONS.md D151.
 *
 * The `Idempotency-Key` is minted here, client-side, rather than by the server — that offline
 * minting is the entire point of §5.1's ULID choice: a phone with no signal has to be able to
 * produce the key a retry needs without asking anyone.
 */

import { api } from "./api";
import { createOutbox, outboxRecordBase, type OutboxRecord } from "./offlineOutbox";

export type QueuedCapture = OutboxRecord & { text: string };

const outbox = createOutbox<QueuedCapture>({
  dbName: "efss-capture-queue",
  storeName: "queue",
  send: (record) => api.capture(record.text, record.idempotencyKey),
});

/**
 * Persist a raw capture and return — §8.1 steps 1 and 2, done entirely on the client since
 * there is no server to hand off to yet.
 */
export async function queueCapture(text: string): Promise<QueuedCapture> {
  const trimmed = text.trim();
  if (!trimmed) throw new Error("nothing to capture");
  const record: QueuedCapture = { ...outboxRecordBase(), text: trimmed };
  await outbox.enqueue(record);
  return record;
}

export const flushCaptureQueue = outbox.flush;
/** For a "N captures waiting to send" indicator, whenever something wants to show one. */
export const pendingCaptureCount = outbox.pendingCount;
export const startCaptureQueueFlushing = outbox.startFlushing;
